import { v4 as uuidv4 } from "uuid";
import { getAssetUrls } from "../../schemas/form/html-content";
export class Step {
    id;
    text;
    start;
    end;
    attributes;
    createdAt;
    constructor(step) {
        this.id = step.id;
        this.text = step.text;
        this.start = step.start;
        this.end = step.end;
        this.attributes = StepAttributesFactory.create(step.attributes);
        this.createdAt = step.createdAt;
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            start: this.start,
            end: this.end,
            attributes: this.attributes.toJSON(),
            createdAt: this.createdAt,
        };
    }
}
export class StepAttributesFactory {
    static getClass(type) {
        const map = {
            filler: FillerStepAttributes,
            userClick: UserClickStepAttributes,
            highlight: HighlightStepAttributes,
            question: MultipleChoiceQuestionStepAttributes,
            modal: MultipleChoiceModalStepAttributes,
            showContent: ShowContentStepAttributes,
            waitForElement: WaitForElementStepAttributes,
            redirect: RedirectStepAttributes,
            autoType: AutoTypeStepAttributes,
            autoClick: AutoClickStepAttributes,
            sleep: SleepStepAttributes,
            drag: DragStepAttributes,
            userInput: UserInputStepAttributes,
        };
        const StepClass = map[type];
        if (!StepClass) {
            throw new Error(`Unknown step attributes type: ${type.type}`);
        }
        return StepClass;
    }
    static create(attributes) {
        const StepClass = this.getClass(attributes.type);
        return new StepClass(attributes);
    }
    static createWithFormDefaults(attributes) {
        const StepClass = this.getClass(attributes.type);
        return StepClass.createWithFormDefaults(attributes);
    }
}
const stepTypes = [
    "filler",
    "userClick",
    "highlight",
    "question",
    "modal",
    "showContent",
    "waitForElement",
    "redirect",
    "autoType",
    "autoClick",
    "drag",
    "sleep",
    "userInput",
];
export function isValidStepType(type) {
    return stepTypes.includes(type);
}
export class OnTrackCondition {
    id;
    left_operand;
    right_operand;
    operator;
    isManaged;
    isCarried;
    isModified;
    carriedFrom;
    constructor(condition) {
        this.id = condition.id ?? uuidv4();
        this.left_operand = condition.left_operand || "selector";
        this.operator = condition.operator || (this.left_operand === "selector" ? "exists" : "contains");
        this.right_operand = condition.right_operand || "";
        this.isManaged = condition.isManaged ?? false;
        this.isCarried = condition.isCarried ?? false;
        this.isModified = condition.isModified ?? false;
        this.carriedFrom = condition.carriedFrom;
    }
    isEqual(condition) {
        return (this.left_operand === condition.left_operand &&
            this.right_operand === condition.right_operand &&
            this.operator === condition.operator);
    }
    isEmpty() {
        return !this.operator || !this.right_operand || this.right_operand.length === 0;
    }
    isUrlCondition() {
        return this.left_operand === "url";
    }
    isSelectorCondition() {
        return this.left_operand === "selector";
    }
    isSupplementarySelectorCondition() {
        return this.isSelectorCondition() && !this.isManaged;
    }
    isTargetSelectorCondition() {
        return this.isSelectorCondition() && this.isManaged;
    }
    isCarriedSelectorCondition() {
        return this.isSelectorCondition() && this.isCarried;
    }
    static empty(leftOperand) {
        return new OnTrackCondition({
            left_operand: leftOperand,
        });
    }
    toJSON() {
        return {
            id: this.id,
            left_operand: this.left_operand,
            operator: this.operator,
            right_operand: this.right_operand,
            isManaged: this.isManaged,
            isCarried: this.isCarried,
            isModified: this.isModified,
        };
    }
}
export class OnTrackConditions {
    isEnabled;
    conditions;
    constructor(conditions) {
        this.isEnabled = conditions.isEnabled;
        this.conditions = conditions.conditions.map((condition) => new OnTrackCondition(condition));
    }
    get nonEmptyConditions() {
        return this.conditions.filter((condition) => !condition.isEmpty());
    }
    get urlCondition() {
        return this.conditions.find((condition) => condition.isUrlCondition());
    }
    get urlConditions() {
        return this.conditions.filter((condition) => condition.isUrlCondition());
    }
    get nonEmptyUrlConditions() {
        return this.urlConditions.filter((condition) => !condition.isEmpty());
    }
    get selectorConditions() {
        return this.conditions.filter((condition) => condition.isSelectorCondition());
    }
    get nonEmptySelectorConditions() {
        return this.selectorConditions.filter((condition) => !condition.isEmpty());
    }
    get selectors() {
        return this.nonEmptySelectorConditions.map((condition) => condition.right_operand);
    }
    isEmpty() {
        return this.conditions.every((condition) => condition.isEmpty());
    }
    toJSON() {
        return {
            isEnabled: this.isEnabled,
            conditions: this.conditions.map((condition) => condition.toJSON()),
        };
    }
}
// Assumes max one managed condition exists
export const partitionManagedOnTrackConditions = (conditions) => {
    if (conditions.length === 0) {
        return [[], []];
    }
    const managedConditionIndex = conditions.findIndex((condition) => condition.isManaged);
    const managedCondition = managedConditionIndex !== -1 ? conditions[managedConditionIndex] : undefined;
    if (!managedCondition) {
        return [[], conditions];
    }
    const rest = conditions.slice(0, managedConditionIndex).concat(conditions.slice(managedConditionIndex + 1));
    return [[managedCondition], rest];
};
export class BaseStepAttributes {
    isCheckpoint;
    onTrackConditions;
    constructor(attributes) {
        this.isCheckpoint = attributes.isCheckpoint ?? true;
        const defaultOnTrackConditions = {
            isEnabled: false,
            conditions: [],
        };
        this.onTrackConditions = new OnTrackConditions(attributes.onTrackConditions ?? defaultOnTrackConditions);
    }
    isTargetable() {
        return false;
    }
    isHighlightable() {
        return false;
    }
    assetUrls = () => [];
    toJSON() {
        return {
            isCheckpoint: this.isCheckpoint,
            onTrackConditions: this.onTrackConditions.toJSON(),
        };
    }
}
export class FillerStepAttributes extends BaseStepAttributes {
    type = "filler";
    constructor(attributes) {
        super(attributes);
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "filler",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
        };
    }
}
export class RedirectStepAttributes extends BaseStepAttributes {
    type = "redirect";
    slug;
    applyConditionsOnNextSteps;
    changeType;
    constructor(attributes) {
        super(attributes);
        this.slug = attributes.slug;
        this.applyConditionsOnNextSteps = attributes.applyConditionsOnNextSteps;
        this.changeType = attributes.changeType || "redirect";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "redirect",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            slug: this.slug,
            applyConditionsOnNextSteps: this.applyConditionsOnNextSteps,
            changeType: this.changeType,
        };
    }
}
export class DragStepAttributes extends BaseStepAttributes {
    type = "drag";
    direction;
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    targetElementHighlightStyle;
    virtualCursor;
    constructor(attributes) {
        super(attributes);
        this.direction = attributes.direction;
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.targetElementHighlightStyle = "pulsingBorder";
        this.virtualCursor = new VirtualCursorSettings(attributes.virtualCursor || { animationType: "drag" });
    }
    isTargetable() {
        return true;
    }
    isHighlightable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "drag",
            direction: attributes.direction ?? "up",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            direction: this.direction,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            virtualCursor: this.virtualCursor.toJSON(),
        };
    }
}
export class UserClickStepAttributes extends BaseStepAttributes {
    type = "userClick";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    targetElementHighlightStyle;
    virtualCursor;
    additionalContent;
    additionalContentState;
    clickDetectionMethod;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.targetElementHighlightStyle = attributes.targetElementHighlightStyle || "pulsingBorder";
        this.virtualCursor = new VirtualCursorSettings(attributes.virtualCursor || { animationType: "move" });
        this.additionalContent = attributes.additionalContent;
        this.additionalContentState = attributes.additionalContentState;
        this.clickDetectionMethod = attributes.clickDetectionMethod || "click";
    }
    isTargetable() {
        return true;
    }
    isHighlightable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "userClick",
            targetElementSelector: attributes.targetElementSelector ?? "",
            clickDetectionMethod: attributes.clickDetectionMethod ?? "click",
        });
    }
    assetUrls = () => getAssetUrls(this.additionalContentState);
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            virtualCursor: this.virtualCursor.toJSON(),
            additionalContent: this.additionalContent,
            additionalContentState: this.additionalContentState,
            clickDetectionMethod: this.clickDetectionMethod,
        };
    }
}
export class HighlightStepAttributes extends BaseStepAttributes {
    type = "highlight";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    targetElementHighlightStyle;
    virtualCursor;
    additionalContent;
    additionalContentState;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.targetElementHighlightStyle = attributes.targetElementHighlightStyle || "backdrop";
        this.virtualCursor = new VirtualCursorSettings(attributes.virtualCursor || { animationType: "hidden" });
        this.additionalContent = attributes.additionalContent;
        this.additionalContentState = attributes.additionalContentState;
    }
    isTargetable() {
        return true;
    }
    isHighlightable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "highlight",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    assetUrls = () => getAssetUrls(this.additionalContentState);
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            virtualCursor: this.virtualCursor.toJSON(),
            additionalContent: this.additionalContent,
            additionalContentState: this.additionalContentState,
        };
    }
}
export class ShowContentStepAttributes extends BaseStepAttributes {
    type = "showContent";
    content;
    contentState;
    constructor(attributes) {
        super(attributes);
        this.content = attributes.content;
        this.contentState = attributes.contentState;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "showContent",
            content: attributes.content ?? "",
        });
    }
    assetUrls = () => getAssetUrls(this.contentState);
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            content: this.content,
            contentState: this.contentState,
        };
    }
}
export class WaitForElementStepAttributes extends BaseStepAttributes {
    type = "waitForElement";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    timeout;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.type = attributes.type;
        this.timeout = attributes.timeout ?? 6000;
    }
    isTargetable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "waitForElement",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            timeout: this.timeout,
            onTrackConditions: this.onTrackConditions.toJSON(),
        };
    }
}
export class UserInputStepAttributes extends BaseStepAttributes {
    type = "userInput";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    targetElementHighlightStyle;
    virtualCursor;
    confirmationPopup;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.targetElementHighlightStyle = attributes.targetElementHighlightStyle || "pulsingBorder";
        this.virtualCursor = new VirtualCursorSettings(attributes.virtualCursor || { animationType: "hidden" });
        this.confirmationPopup = attributes.confirmationPopup || {};
        this.confirmationPopup.position = this.confirmationPopup.position ?? "bottom";
    }
    isTargetable() {
        return true;
    }
    isHighlightable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "userInput",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            virtualCursor: this.virtualCursor.toJSON(),
            confirmationPopup: this.confirmationPopup,
        };
    }
}
export class SleepStepAttributes extends BaseStepAttributes {
    type = "sleep";
    duration;
    constructor(attributes) {
        super(attributes);
        this.duration = attributes.duration;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "sleep",
            duration: attributes.duration ?? 0,
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            duration: this.duration,
        };
    }
}
export class VirtualCursorSettings {
    animationType;
    constructor(settings) {
        this.animationType = settings?.animationType ?? "move";
    }
    toJSON() {
        return {
            animationType: this.animationType,
        };
    }
}
export class MultipleChoiceQuestionStepAttributes extends BaseStepAttributes {
    type = "question";
    questionType = "multipleChoice";
    choices;
    title;
    text;
    additionalContent;
    additionalContentState;
    constructor(attributes) {
        super(attributes);
        this.choices = QuestionChoiceFactory.createList(attributes.choices);
        this.text = attributes.text;
        this.title = attributes.title;
        this.additionalContent = attributes.additionalContent;
        this.additionalContentState = attributes.additionalContentState;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "question",
            questionType: "multipleChoice",
            choices: attributes.choices ?? [],
            title: attributes.title ?? "",
        });
    }
    assetUrls = () => getAssetUrls(this.additionalContentState);
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            questionType: this.questionType,
            choices: this.choices.map((choice) => choice.toJSON()),
            title: this.title,
            text: this.text,
            additionalContent: this.additionalContent,
            additionalContentState: this.additionalContentState,
        };
    }
}
export class QuestionChoiceFactory {
    static create(choice) {
        switch (choice.type) {
            case "link":
                return new LinkChoice(choice);
            case "proceedNext":
                return new ProceedNextChoice(choice);
            case "suspendTour":
                return new SuspendTourChoice(choice);
            case "finishTour":
                return new FinishTourChoice(choice);
            case "switchTour":
                return new SwitchTourChoice(choice);
            case "triggerClick":
                return new TriggerClickChoice(choice);
            case "contactSupport":
                return new ContactSupportChoice(choice);
            case "loadCheckpoint":
                return new LoadCheckpointChoice(choice);
            case "dismissTour":
                return new DismissTourChoice(choice);
            default:
                throw new Error(`Unknown question choice type: ${choice.type}`);
        }
    }
    static createList(choices) {
        if (!choices) {
            return [];
        }
        return choices.map((choice) => QuestionChoiceFactory.create(choice));
    }
}
export class LinkChoice {
    type = "link";
    id;
    text;
    url;
    buttonVariant;
    windowType;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.url = choice.url;
        this.windowType = choice.windowType || "newTab";
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "link",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            url: attributes.url ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            url: this.url,
            windowType: this.windowType,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class ProceedNextChoice {
    type = "proceedNext";
    id;
    text;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "proceedNext",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class SuspendTourChoice {
    type = "suspendTour";
    id;
    text;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "suspendTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class FinishTourChoice {
    type = "finishTour";
    id;
    text;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "finishTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class SwitchTourChoice {
    type = "switchTour";
    id;
    text;
    buttonVariant;
    guideId;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
        this.guideId = choice.guideId;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "switchTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            guideId: attributes.guideId ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
            guideId: this.guideId,
        };
    }
}
export class TriggerClickChoice {
    type = "triggerClick";
    id;
    text;
    selector;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.selector = choice.selector;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "triggerClick",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            selector: attributes.selector ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            selector: this.selector,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class DismissTourChoice {
    type = "dismissTour";
    id;
    text;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "dismissTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class ContactSupportChoice {
    type = "contactSupport";
    id;
    text;
    url;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.url = choice.url;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "contactSupport",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            url: attributes.url ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            url: this.url,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class LoadCheckpointChoice {
    type = "loadCheckpoint";
    id;
    text;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "loadCheckpoint",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class FinishTourModalChoice {
    type = "finishTour";
    id;
    text;
    buttonVariant;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "finishTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class ProceedNextModalChoice {
    type = "proceedNext";
    id;
    text;
    buttonVariant;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "proceedNext",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class ContactSupportModalChoice {
    type = "contactSupport";
    id;
    text;
    buttonVariant;
    url;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.url = option.url;
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "contactSupport",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            url: attributes.url ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            url: this.url,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class LinkModalChoice {
    type = "link";
    id;
    text;
    url;
    buttonVariant;
    windowType;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.url = option.url;
        this.windowType = option.windowType || "newTab";
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "link",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            url: attributes.url ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            url: this.url,
            windowType: this.windowType,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class SwitchTourModalChoice {
    type = "switchTour";
    id;
    text;
    guideId;
    buttonVariant;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.guideId = option.guideId;
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "switchTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            guideId: attributes.guideId ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            guideId: this.guideId,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class SuspendTourModalChoice {
    type = "suspendTour";
    id;
    text;
    buttonVariant;
    constructor(option) {
        this.id = option.id;
        this.text = option.text;
        this.buttonVariant = option.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "suspendTour",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class TriggerClickModalChoice {
    type = "triggerClick";
    id;
    text;
    selector;
    buttonVariant;
    constructor(choice) {
        this.id = choice.id;
        this.text = choice.text;
        this.selector = choice.selector;
        this.buttonVariant = choice.buttonVariant || "primary";
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "triggerClick",
            id: attributes.id ?? uuidv4(),
            text: attributes.text ?? "",
            selector: attributes.selector ?? "",
        });
    }
    toJSON() {
        return {
            id: this.id,
            text: this.text,
            type: this.type,
            selector: this.selector,
            buttonVariant: this.buttonVariant,
        };
    }
}
export class ModalChoiceFactory {
    static create(option) {
        switch (option.type) {
            case "finishTour":
                return new FinishTourModalChoice(option);
            case "proceedNext":
                return new ProceedNextModalChoice(option);
            case "link":
                return new LinkModalChoice(option);
            case "contactSupport":
                return new ContactSupportModalChoice(option);
            case "switchTour":
                return new SwitchTourModalChoice(option);
            case "suspendTour":
                return new SuspendTourModalChoice(option);
            case "triggerClick":
                return new TriggerClickModalChoice(option);
            default:
                throw new Error(`Unknown question choice type: ${option.type}`);
        }
    }
    static createList(options) {
        if (!options) {
            return [];
        }
        return options.map((opt) => ModalChoiceFactory.create(opt));
    }
}
export class MultipleChoiceModalStepAttributes extends BaseStepAttributes {
    type = "modal";
    questionType;
    choices;
    title;
    description;
    modalContent;
    modalContentState;
    variant;
    constructor(attributes) {
        super(attributes);
        this.questionType = "multipleChoice";
        this.choices = ModalChoiceFactory.createList(attributes.choices);
        this.title = attributes.title;
        this.description = attributes.description;
        this.modalContent = attributes.modalContent;
        this.modalContentState = attributes.modalContentState;
        this.variant = attributes.variant;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "modal",
            questionType: "multipleChoice",
            title: attributes.title ?? "",
            modalContent: attributes.modalContent ?? "",
            choices: attributes.choices ?? [],
        });
    }
    assetUrls = () => getAssetUrls(this.modalContentState);
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            questionType: this.questionType,
            choices: this.choices.map((choice) => choice.toJSON()),
            title: this.title,
            description: this.description,
            modalContent: this.modalContent,
            modalContentState: this.modalContentState,
            variant: this.variant,
        };
    }
}
export class AutoTypeStepAttributes extends BaseStepAttributes {
    type = "autoType";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    instantPaste;
    targetElementHighlightStyle;
    text;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector ?? "";
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.targetElementHighlightStyle = attributes.targetElementHighlightStyle || "pulsingPoint";
        this.text = attributes.text;
        this.instantPaste = attributes.instantPaste || false;
    }
    isTargetable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "autoType",
            text: attributes.text ?? "",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            text: this.text,
            instantPaste: this.instantPaste,
            onTrackConditions: this.onTrackConditions.toJSON(),
        };
    }
}
export class AutoClickStepAttributes extends BaseStepAttributes {
    type = "autoClick";
    targetElementSelector;
    targetElementSelectorRank;
    targetScreenshot;
    targetRoute;
    targetElementHighlightStyle;
    virtualCursor;
    constructor(attributes) {
        super(attributes);
        this.targetElementSelector = attributes.targetElementSelector;
        this.targetElementSelectorRank = attributes.targetElementSelectorRank;
        this.targetScreenshot = attributes.targetScreenshot;
        this.targetRoute = attributes.targetRoute;
        this.virtualCursor = new VirtualCursorSettings(attributes.virtualCursor || { animationType: "move" });
        this.targetElementHighlightStyle = attributes.targetElementHighlightStyle || "pulsingPoint";
    }
    isTargetable() {
        return true;
    }
    isHighlightable() {
        return true;
    }
    static createWithFormDefaults(attributes) {
        return new this({
            ...attributes,
            type: "autoClick",
            targetElementSelector: attributes.targetElementSelector ?? "",
        });
    }
    toJSON() {
        return {
            ...super.toJSON(),
            type: this.type,
            targetRoute: this.targetRoute,
            targetScreenshot: this.targetScreenshot,
            targetElementSelector: this.targetElementSelector,
            targetElementSelectorRank: this.targetElementSelectorRank,
            targetElementHighlightStyle: this.targetElementHighlightStyle,
            virtualCursor: this.virtualCursor.toJSON(),
            onTrackConditions: this.onTrackConditions.toJSON(),
        };
    }
}
